import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Divider, FormControlLabel, Stack, Tooltip,
  Typography, Alert, LinearProgress,
  Box,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
} from '@mui/material'
import { FormInput, SearchBar } from '../../components/ui'
import { EntityFormSection, EntityDrawerInput } from '../../components/shared/EntityFormDrawer'
import {
  ArrowBack as BackIcon,
  Shield as ShieldIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  People as PeopleIcon,
  VpnKey as KeyIcon,
  CheckBox as CheckAllIcon,
  CheckBoxOutlineBlank as UncheckAllIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { PageHeader } from '../../components/shared/PageHeader'
import { rolesApi, Permission, UpdateRolePayload } from '../../lib/api/rolesApi'
import { themeColors } from '../../app/layout/theme'

// ── Permission group labels ───────────────────────────────────────────────────
const GROUP_LABELS: Record<string, string> = {
  // Core CRM
  lead:           'Lead Management',
  deal:           'Deal Management',
  contact:        'Contact Management',
  customer:       'Customer Management',
  sales:          'Sales Management',
  // Productivity
  task:           'Task Management',
  calendar:       'Calendar',
  activity:       'Activity Feed',
  notification:   'Notifications',
  // Pipeline & Automation
  pipeline_stage: 'Pipeline Stages',
  workflow:       'Workflow Automation',
  // Communication
  channel:        'Channels & Integrations',
  email_file:     'Email Template Files',
  // Admin
  user:           'User Management',
  role:           'Role Management',
  permission:     'Permission Management',
  tag:            'Tag Management',
  // Analytics & Settings
  report:         'Reports & Analytics',
  dashboard:      'Dashboard',
  settings:       'Settings',
}

// Action colour coding
const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  read:          { bg: '#e8f5e9', color: '#2e7d32' },
  create:        { bg: '#e3f2fd', color: '#1565c0' },
  update:        { bg: '#fff8e1', color: '#f57f17' },
  delete:        { bg: '#fce4ec', color: '#c62828' },
  assign:        { bg: '#f3e5f5', color: '#6a1b9a' },
  convert:       { bg: '#fff3e0', color: '#e65100' },
  toggle:        { bg: '#e8eaf6', color: '#283593' },
  manage:        { bg: '#fbe9e7', color: '#bf360c' },
  manage_stage:  { bg: '#fff3e0', color: '#e65100' },
  change_status: { bg: '#e0f7fa', color: '#006064' },
  bulk_upload:   { bg: '#f1f8e9', color: '#33691e' },
  upload:        { bg: '#f1f8e9', color: '#33691e' },
  update_own:    { bg: '#fff8e1', color: '#f57f17' },
  update_intel:  { bg: '#fff8e1', color: '#f57f17' },
  update_pipeline:{ bg: '#e0f2fe', color: '#0369a1' },
  update_order: { bg: '#ede7f6', color: '#512da8' },
}

function groupPermissions(permissions: Permission[]) {
  const groups: Record<string, Permission[]> = {}
  for (const p of permissions) {
    const resource = p.action.split(':')[0] ?? 'other'
    if (!groups[resource]) groups[resource] = []
    groups[resource].push(p)
  }
  return groups
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function RoleEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [permSearch, setPermSearch] = useState('')

  // ── Data fetching ─────────────────────────────────────────────────────────
  const isNew = id === 'new'

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['role', id],
    queryFn: () => rolesApi.getById(id!),
    enabled: !isNew && !!id,
  })

  const { data: allPermissions = [], isLoading: permsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: rolesApi.getAllPermissions,
  })

  // Seed form from fetched role
  useEffect(() => {
    if (role) {
      setName(role.name)
      setDescription(role.description ?? '')
      setSelectedIds(new Set(role.permissions.map((rp) => rp.permission.id)))
    }
  }, [role])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      toast.success('Role created')
      navigate('/admin/roles')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create role'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ payload }: { payload: UpdateRolePayload }) => rolesApi.update(id!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      qc.invalidateQueries({ queryKey: ['role', id] })
      toast.success('Role updated')
      navigate('/admin/roles')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update role'),
  })

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isSuperAdmin = role?.name === 'SUPER_ADMIN'

  // ── Permission helpers ────────────────────────────────────────────────────
  const filteredPermissions = useMemo(() => {
    if (!permSearch.trim()) return allPermissions
    return allPermissions.filter((p) =>
      p.action.toLowerCase().includes(permSearch.toLowerCase()) ||
      (p.description ?? '').toLowerCase().includes(permSearch.toLowerCase())
    )
  }, [allPermissions, permSearch])

  const grouped = useMemo(() => groupPermissions(filteredPermissions), [filteredPermissions])

  const togglePermission = (pid: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(pid) ? next.delete(pid) : next.add(pid)
      return next
    })
  }

  const toggleGroup = (perms: Permission[]) => {
    const allSelected = perms.every((p) => selectedIds.has(p.id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      perms.forEach((p) => (allSelected ? next.delete(p.id) : next.add(p.id)))
      return next
    })
  }

  const selectAll = () => setSelectedIds(new Set(allPermissions.map((p) => p.id)))
  const clearAll = () => setSelectedIds(new Set())

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!name.trim()) { setNameError('Role name is required'); return }
    if (name.trim().length < 2) { setNameError('At least 2 characters'); return }
    setNameError('')

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      permissionIds: [...selectedIds],
    }

    if (isNew) {
      createMutation.mutate(payload)
    } else {
      updateMutation.mutate({ payload })
    }
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (!isNew && (roleLoading || permsLoading)) {
    return (
      <Box>
        <PageHeader
          title="Edit Role"
          breadcrumb={[
            { label: 'Settings', to: '/settings' },
            { label: 'Roles', to: '/admin/roles' },
          ]}
          showBreadcrumb
          icon={<ShieldIcon />}
        />
        <LinearProgress />
      </Box>
    )
  }

  const roleColor =
    isSuperAdmin ? themeColors.error :
      name === 'ADMIN' ? '#7c3aed' :
        name === 'MANAGER' ? '#0070d2' :
          themeColors.primary

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Create Role' : `Edit Role — ${role?.name ?? ''}`}
        subtitle={isNew ? 'Define a new role and assign permissions' : 'Update role details and permissions'}
        breadcrumb={[
          { label: 'Settings', to: '/settings' },
          { label: 'Roles', to: '/admin/roles' },
        ]}
        showBreadcrumb
        icon={<ShieldIcon />}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/admin/roles')}
              sx={{ borderRadius: '8px', fontSize: '13px' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? undefined : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
              sx={{ borderRadius: '8px', fontSize: '13px', background: themeColors.primary, '&:hover': { background: themeColors.primaryDark } }}
            >
              {isSaving
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : isNew ? 'Create Role' : 'Save Changes'}
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '320px 1fr' }, gap: 3, alignItems: 'start' }}>

        {/* ── Left: Role info ── */}
        <Stack spacing={2.5}>
          <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${roleColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldIcon sx={{ fontSize: 22, color: roleColor }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: themeColors.text.primary }}>
                  Role Details
                </Typography>
              </Box>

              <EntityFormSection columns={1}>
                <EntityDrawerInput
                  label="Role Name"
                  value={name}
                  onChange={(v) => { setName(v.toUpperCase().replace(/\s+/g, '_')); if (nameError) setNameError('') }}
                  error={nameError}
                  placeholder="e.g. SALES_REP, SUPPORT_AGENT"
                  disabled={isSuperAdmin}
                  required
                />
                <EntityDrawerInput
                  label="Description"
                  value={description}
                  onChange={(v) => setDescription(v)}
                  multiline
                  rows={3}
                  placeholder="Brief description of this role's purpose"
                />
              </EntityFormSection>

              {isSuperAdmin && (
                <Alert severity="warning" sx={{ mt: 2, fontSize: '12px', borderRadius: '8px' }}>
                  SUPER_ADMIN is a system role. Name cannot be changed and it bypasses all permission checks.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Stats (edit only) */}
          {!isNew && role && (
            <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary, mb: 2 }}>
                  Stats
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <KeyIcon sx={{ fontSize: 16, color: themeColors.text.muted }} />
                      <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>Permissions</Typography>
                    </Box>
                    <Chip label={selectedIds.size} size="small" sx={{ height: 22, fontSize: '12px', fontWeight: 700, bgcolor: themeColors.primaryLight, color: '#ffffff' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon sx={{ fontSize: 16, color: themeColors.text.muted }} />
                      <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>Assigned users</Typography>
                    </Box>
                    <Chip label={role.count.users} size="small" sx={{ height: 22, fontSize: '12px', fontWeight: 700, bgcolor: '#f0f0f0', color: themeColors.text.secondary }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>

        {/* ── Right: Permissions panel ── */}
        <Card sx={{ borderRadius: '14px', border: `1px solid ${themeColors.border}`, boxShadow: themeColors.shadow.sm }}>
          <CardContent sx={{ p: 3 }}>

            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: themeColors.text.primary }}>
                  Permissions
                </Typography>
                <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mt: '2px' }}>
                  {selectedIds.size} of {allPermissions.length} selected
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Select all permissions">
                  <Button
                    size="small"
                    startIcon={<CheckAllIcon sx={{ fontSize: 15 }} />}
                    onClick={selectAll}
                    disabled={isSuperAdmin}
                    sx={{ fontSize: '12px', borderRadius: '6px', color: themeColors.primary }}
                  >
                    All
                  </Button>
                </Tooltip>
                <Tooltip title="Clear all permissions">
                  <Button
                    size="small"
                    startIcon={<UncheckAllIcon sx={{ fontSize: 15 }} />}
                    onClick={clearAll}
                    disabled={isSuperAdmin}
                    sx={{ fontSize: '12px', borderRadius: '6px', color: themeColors.text.secondary }}
                  >
                    None
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            <SearchBar
              placeholder="Search permissions..."
              value={permSearch}
              onChange={setPermSearch}
              sx={{ mb: 2.5 }}
            />

            {permsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : Object.keys(grouped).length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: themeColors.text.secondary }}>
                <KeyIcon sx={{ fontSize: 36, opacity: 0.2, mb: 1 }} />
                <Typography sx={{ fontSize: '13px' }}>No permissions match your search</Typography>
              </Box>
            ) : (
              <Stack spacing={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                {Object.entries(grouped).map(([resource, perms], idx) => {
                  const allChecked = perms.every((p) => selectedIds.has(p.id))
                  const someChecked = perms.some((p) => selectedIds.has(p.id))
                  const groupLabel = GROUP_LABELS[resource] ?? `${resource.charAt(0).toUpperCase()}${resource.slice(1)} Management`
                  const checkedCount = perms.filter((p) => selectedIds.has(p.id)).length

                  return (
                    <Box key={resource}>
                      {idx > 0 && <Divider />}
                      <Box sx={{ px: 2.5, py: 2, backgroundColor: allChecked ? `${themeColors.primary}06` : idx % 2 === 0 ? '#fafafa' : '#fff' }}>

                        {/* Group header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={allChecked}
                                indeterminate={!allChecked && someChecked}
                                onChange={() => toggleGroup(perms)}
                                disabled={isSuperAdmin}
                                sx={{ '&.Mui-checked': { color: themeColors.primary }, '&.MuiCheckbox-indeterminate': { color: themeColors.primary } }}
                              />
                            }
                            label={
                              <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: themeColors.text.primary }}>
                                {groupLabel}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <Chip
                            label={`${checkedCount}/${perms.length}`}
                            size="small"
                            sx={{
                              height: 20, fontSize: '11px', fontWeight: 600,
                              bgcolor: checkedCount > 0 ? themeColors.primaryLight : '#f0f0f0',
                              color: checkedCount > 0 ? themeColors.primary : themeColors.text.muted,
                            }}
                          />
                        </Box>

                        {/* Individual permissions as rows */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 0.5, pl: 1 }}>
                          {perms.map((perm) => {
                            const action = perm.action.split(':')[1] ?? perm.action
                            const checked = selectedIds.has(perm.id)
                            const style = ACTION_COLORS[action] ?? { bg: themeColors.primaryLight, color: themeColors.primary }

                            return (
                              <FormControlLabel
                                key={perm.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    onChange={() => togglePermission(perm.id)}
                                    disabled={isSuperAdmin}
                                    sx={{ py: '4px', '&.Mui-checked': { color: themeColors.primary } }}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <Chip
                                      label={action}
                                      size="small"
                                      sx={{
                                        height: 18, fontSize: '10.5px', fontWeight: 600,
                                        bgcolor: checked ? style.bg : '#f0f0f0',
                                        color: checked ? style.color : themeColors.text.muted,
                                        borderRadius: '4px', transition: 'all 0.15s',
                                      }}
                                    />
                                    {perm.description && (
                                      <Typography sx={{ fontSize: '11.5px', color: themeColors.text.secondary }}>
                                        {perm.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                sx={{ m: 0 }}
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    </Box>
                  )
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
